import { MagnifyingGlassIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../ui/Spinner';
import { pickedRecordName } from '../../data/displayValue';
import { parsePickedRecord } from '../../data/referenceDefaults';
import { lookup, type LookupKind } from '../../data/lookup';
import { getMyRegionHint } from '../../data/me';
import type { LookupResult } from '../../data/types';

interface RecordPickerProps {
  id: string;
  kind: LookupKind;
  invalid: boolean;
  /** The chosen record, stored as JSON so generation never re-queries the API. */
  value: string;
  onChange: (value: string) => void;
  /** Lets a name absent from the client data API be entered as plain text instead. */
  allowCustomValue?: boolean;
  /** Project/Contact only: a Linked Company property is configured (whether or not it has a value yet). */
  linkedCompanyConfigured?: boolean;
  /** Project/Contact only: the resolved company id from that property, empty until the user picks one (or types a custom company name with no matching record). */
  linkedCompanyId?: string;
  /** Project/Contact only: the linked Company property's raw value — used to tell "not chosen yet" apart from "chosen but typed by hand", since the latter has no id. */
  linkedCompanyValue?: string;
}

/** Long enough that the first-time fetch of the whole list is worth announcing. */
const SLOW_SEARCH_MS = 1200;

/** The secondary detail shown next to a result; company name is shown separately (in grey). */
function describeDetail(record: LookupResult, hideCountry: boolean): string {
  return record.jobTitle ?? (hideCountry ? undefined : record.country) ?? record.projectNumber ?? record.email ?? '';
}

export function RecordPicker({
  id,
  kind,
  invalid,
  value,
  onChange,
  allowCustomValue,
  linkedCompanyConfigured = false,
  linkedCompanyId = '',
  linkedCompanyValue = ''
}: RecordPickerProps) {
  const { t } = useTranslation();
  const listId = useId();

  const [query, setQuery] = useState('');
  const [country, setCountry] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);

  // A configured Linked Company with no value at all means there is nothing to search for yet.
  // A manually typed company name has no id but still counts as chosen, so it must not block this field.
  const linkedCompanyPending = linkedCompanyConfigured && linkedCompanyValue.trim() === '';
  // Shown only when there is no Linked Company restricting the results to one company already.
  const showCompanyName = (kind === 'projects' || kind === 'contacts') && !linkedCompanyConfigured;
  // Once the company is known, there is a small, bounded set to choose from — show it all rather than making the user type.
  const companyScoped = (kind === 'projects' || kind === 'contacts') && linkedCompanyId !== '';

  // Every settled search, keyed by kind/company/country/query. Doubles as the source of
  // truth for the results, so re-reaching a query renders it without a round trip.
  const [settled, setSettled] = useState<ReadonlyMap<string, LookupResult[]>>(new Map());
  const [failedKey, setFailedKey] = useState('');
  const [slowKey, setSlowKey] = useState('');

  const wrapper = useRef<HTMLDivElement>(null);

  // A record travels as JSON; anything else is a value typed by hand rather than picked.
  const isManualValue = allowCustomValue === true && value.trim() !== '' && !value.trimStart().startsWith('{');
  const chosenRecord = parsePickedRecord(value);
  const chosen = pickedRecordName(value) || (isManualValue ? value.trim() : '');
  const chosenCompanyName = showCompanyName ? (chosenRecord?.companyname ?? '') : '';
  const trimmed = query.trim();
  const searchable = !chosen && !linkedCompanyPending && (companyScoped || trimmed.length >= 2);

  // Derived, so the spinner shows from the first keystroke rather than after the debounce.
  const key = searchable ? `${kind}|${linkedCompanyId}|${country}|${trimmed}` : '';
  const results = settled.get(key) ?? [];
  const failed = key !== '' && failedKey === key;
  const pending = key !== '' && !settled.has(key) && !failed;
  const slow = pending && slowKey === key;
  const showPanel = open && searchable;

  useEffect(() => {
    if (chosen || linkedCompanyPending) return;

    // The server caches the whole list, so fetching it while the user is still
    // reading the label spares them the cold-cache wait on their first search.
    void lookup(kind, '', '', linkedCompanyId).then(
      (response) => setCountries((current) => (current.length > 0 ? current : response.countries)),
      () => undefined
    );

    void getMyRegionHint().then((hint) => {
      if (hint.country) setCountry((current) => current || hint.country!);
    });
  }, [chosen, kind, linkedCompanyId, linkedCompanyPending]);

  useEffect(() => {
    if (key === '' || settled.has(key)) return;

    const slowTimer = setTimeout(() => setSlowKey(key), SLOW_SEARCH_MS);

    // Debounced: the list is server-filtered, but not on every keystroke.
    const timer = setTimeout(() => {
      lookup(kind, trimmed, country, linkedCompanyId)
        .then((response) => {
          // Keyed, so a response that lands after a newer one cannot overwrite it.
          setSettled((current) => new Map(current).set(key, response.items));
          setCountries(response.countries);
        })
        .catch(() => setFailedKey(key));
    }, 250);

    return () => {
      clearTimeout(timer);
      clearTimeout(slowTimer);
    };
  }, [key, kind, trimmed, country, linkedCompanyId, settled]);


  useEffect(() => {
    if (!showPanel) return;

    const dismiss = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [showPanel]);

  const select = (record: LookupResult) => {
    onChange(JSON.stringify(record));
    setOpen(false);
    setHighlighted(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (results.length === 0) return;
      event.preventDefault();
      setOpen(true);
      setHighlighted((current) => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        return (next + results.length) % results.length;
      });
      return;
    }

    if (event.key === 'Enter' && highlighted >= 0 && results[highlighted]) {
      event.preventDefault();
      select(results[highlighted]);
    }
  };

  if (chosen) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 rounded-sm border border-input bg-surface px-3 py-2 text-sm">
        <span className="truncate text-foreground">
          {chosen}
          {chosenCompanyName ? <span className="ml-1.5 text-muted-foreground">· {chosenCompanyName}</span> : null}
        </span>
        <button
          type="button"
          onClick={() => {
            onChange('');
            setQuery('');
          }}
          aria-label={t('wizard.form.clearSelection')}
          className="shrink-0 rounded p-0.5 text-muted-foreground transition hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <XMarkIcon className="size-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  if (linkedCompanyPending) {
    return (
      <p className="mt-1 rounded-sm border border-dashed border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        {t('wizard.form.chooseCompanyFirst')}
      </p>
    );
  }

  return (
    <div ref={wrapper} className="relative mt-1">
      <div className="relative">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          id={id}
          type="search"
          role="combobox"
          value={query}
          autoComplete="off"
          aria-invalid={invalid}
          aria-busy={pending}
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={highlighted >= 0 ? `${listId}-${highlighted}` : undefined}
          placeholder={t(
            allowCustomValue
              ? 'wizard.form.searchOrTypePlaceholder'
              : companyScoped
                ? 'wizard.form.chooseOrSearchPlaceholder'
                : 'wizard.form.searchPlaceholder'
          )}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setHighlighted(-1);
          }}
          className={`w-full rounded-sm border bg-surface py-2 pl-9 pr-9 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22 ${
            invalid ? 'border-destructive' : 'border-input'
          }`}
        />
        {pending ? (
          <Spinner className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        ) : null}
      </div>

      {allowCustomValue ? (
        <p className="mt-1 text-xs text-muted-foreground">{t('wizard.form.customValueHint')}</p>
      ) : null}

      {showPanel ? (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-sm border border-border bg-surface shadow-lg">
          {countries.length > 1 ? (
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <label className="text-xs font-medium text-muted-foreground" htmlFor={`${listId}-country`}>
                {t('wizard.form.countryFilter')}
              </label>
              <select
                id={`${listId}-country`}
                value={country}
                onChange={(event) => {
                  setCountry(event.target.value);
                  setHighlighted(-1);
                }}
                className="min-w-0 flex-1 rounded-sm border border-input bg-surface px-2 py-1 text-xs text-foreground outline-none focus:border-ring"
              >
                <option value="">{t('wizard.form.allCountries')}</option>
                {countries.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div role="status" aria-live="polite">
            {pending ? (
              <p className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                <Spinner className="size-3.5 shrink-0" />
                <span>{slow ? t('wizard.form.firstSearchHint') : t('wizard.form.searching')}</span>
              </p>
            ) : failed ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">{t('wizard.form.lookupUnavailable')}</p>
            ) : results.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">{t('wizard.form.noResults')}</p>
            ) : null}
          </div>

          {results.length > 0 ? (
            <ul id={listId} role="listbox">
              {results.map((record, index) => (
                <li key={record.id} role="option" id={`${listId}-${index}`} aria-selected={index === highlighted}>
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => select(record)}
                    onMouseEnter={() => setHighlighted(index)}
                    className={`w-full px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring ${
                      index === highlighted ? 'bg-muted' : ''
                    }`}
                  >
                    {record.name}
                    {describeDetail(record, country !== '') ? (
                      <span className="text-muted-foreground"> — {describeDetail(record, country !== '')}</span>
                    ) : null}
                    {showCompanyName && record.companyName ? (
                      <span className="text-muted-foreground"> · {record.companyName}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {allowCustomValue && trimmed.length > 0 ? (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => {
                onChange(trimmed);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 border-t border-border bg-muted/40 px-3 py-2 text-left text-sm font-medium text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
            >
              <PlusIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate">{t('wizard.form.useAsEntered', { value: trimmed })}</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
